import React, { useState, useEffect } from 'react';
import {
  Card,
  Row,
  Col,
  Table,
  Button,
  Space,
  Tag,
  Modal,
  Form,
  Input,
  Select,
  InputNumber,
  message,
  Statistic,
  Tabs,
  DatePicker,
  Descriptions,
  Typography,
  Tooltip,
  Divider
} from 'antd';
import {
  DollarOutlined,
  WalletOutlined,
  BankOutlined,
  TransactionOutlined,
  HistoryOutlined,
  ReloadOutlined,
  PlusOutlined,
  MinusOutlined,
  InfoCircleOutlined,
  DownloadOutlined
} from '@ant-design/icons';
import axios from 'axios';
import moment from 'moment';

// Create axios instance for admin API
const adminApi = axios.create({
  baseURL: process.env.REACT_APP_BASE_URL || '/api',
  timeout: 10000,
});

// Request interceptor to add JWT token
adminApi.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('access_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    config.headers['Content-Type'] = 'application/json';
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle errors
adminApi.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('access_token');
      window.location.href = '/admin/login';
    }
    return Promise.reject(error);
  }
);

const { RangePicker } = DatePicker;
const { Option } = Select;
const { TabPane } = Tabs;
const { Title, Text } = Typography;

const MerchantFinance = () => {
  const [loading, setLoading] = useState(false);
  const [balanceData, setBalanceData] = useState(null);
  const [depositTransactions, setDepositTransactions] = useState([]);
  const [settlementSchedule, setSettlementSchedule] = useState([]);
  const [members, setMembers] = useState([]);
  const [withdrawalModalVisible, setWithdrawalModalVisible] = useState(false);
  const [depositModalVisible, setDepositModalVisible] = useState(false);
  const [selectedMember, setSelectedMember] = useState(null);
  const [mutationModalVisible, setMutationModalVisible] = useState(false);
  const [mutationMember, setMutationMember] = useState(null);
  const [mutations, setMutations] = useState([]);
  const [mutationLoading, setMutationLoading] = useState(false);
  const [mutationDateRange, setMutationDateRange] = useState(null);
  const [mutationTotal, setMutationTotal] = useState(0);
  const [filters, setFilters] = useState({
    dateRange: null,
    status: null,
    type: null
  });

  const [withdrawalForm] = Form.useForm();
  const [depositForm] = Form.useForm();

  useEffect(() => {
    fetchFinanceData();
  }, []);

  const fetchFinanceData = async () => {
    setLoading(true);
    try {
      // Fetch members with balance data
      const membersResponse = await adminApi.get('/members', {
        params: { limit: 1000 }
      });
      setMembers(membersResponse.data.members || []);

      // Calculate total statistics from members data
      const membersData = membersResponse.data.members || [];
      const stats = {
        total_deposit_balance: membersData.reduce((sum, member) => sum + parseFloat(member.deposit_balance || 0), 0),
        total_available_balance: membersData.reduce((sum, member) => sum + parseFloat(member.available_balance || 0), 0),
        total_pending_balance: membersData.reduce((sum, member) => sum + parseFloat(member.pending_balance || 0), 0),
        total_frozen_balance: membersData.reduce((sum, member) => sum + parseFloat(member.frozen_balance || 0), 0),
        total_earned: membersData.reduce((sum, member) => sum + parseFloat(member.total_earned || 0), 0),
        total_spent: membersData.reduce((sum, member) => sum + parseFloat(member.total_spent || 0), 0)
      };

      setBalanceData(stats);

    } catch (error) {
      message.error('Gagal memuat data keuangan');
      console.error('Fetch finance data error:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0
    }).format(amount || 0);
  };

  const loadMutations = async (member, range) => {
    setMutationLoading(true);
    try {
      const hasRange = range && range[0] && range[1];
      const params = { page: 1, limit: hasRange ? 5000 : 200 };
      if (hasRange) {
        params.date_from = range[0].format('YYYY-MM-DD');
        params.date_to = range[1].format('YYYY-MM-DD');
      }
      const res = await adminApi.get(`/admin/merchants/${member.id}/balance/history`, { params });
      setMutations(res.data.history || []);
      setMutationTotal(res.data.total || 0);
    } catch (error) {
      message.error('Gagal memuat mutasi saldo');
    } finally {
      setMutationLoading(false);
    }
  };

  const handleViewMutations = (member) => {
    setMutationMember(member);
    setMutationDateRange(null);
    setMutationModalVisible(true);
    setMutations([]);
    setMutationTotal(0);
    loadMutations(member, null);
  };

  // Unduh seluruh mutasi saldo mitra yang sedang dibuka sebagai CSV.
  const handleExportAdminMutations = async () => {
    if (!mutationMember) return;
    const hasRange = mutationDateRange && mutationDateRange[0] && mutationDateRange[1];
    if (!hasRange && mutationTotal > 20000) {
      message.warning(`Mitra ini punya ${mutationTotal.toLocaleString('id-ID')} mutasi — terlalu banyak untuk sekali unduh. Pilih rentang tanggal dulu.`);
      return;
    }
    try {
      const params = { page: 1, limit: 100000 };
      if (hasRange) {
        params.date_from = mutationDateRange[0].format('YYYY-MM-DD');
        params.date_to = mutationDateRange[1].format('YYYY-MM-DD');
      }
      const res = await adminApi.get(`/admin/merchants/${mutationMember.id}/balance/history`, { params });
      const rows = (res.data && res.data.history) || [];
      if (!rows.length) {
        message.warning('Tidak ada data mutasi untuk diunduh');
        return;
      }
      const esc = (v) => `"${String(v === null || v === undefined ? '' : v).replace(/"/g, '""')}"`;
      const header = ['Tanggal', 'Jenis', 'Jumlah', 'Saldo Sebelum', 'Saldo Sesudah', 'Keterangan', 'Referensi', 'ID Member', 'Nama'];
      const lines = rows.map((r) => [
        r.created_at, r.transaction_type, r.amount_change, r.previous_balance, r.new_balance,
        r.description || '', r.reference_id || '', r.member_id || '', r.full_name || ''
      ].map(esc).join(','));
      const csv = '﻿' + [header.map(esc).join(','), ...lines].join('\n');
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      const namaFile = String(mutationMember.full_name || mutationMember.member_id || mutationMember.id || 'mitra').replace(/[^a-zA-Z0-9]+/g, '_');
      a.download = `mutasi_saldo_${namaFile}_${moment().format('YYYY-MM-DD_HH-mm-ss')}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(a.href);
      message.success(`${rows.length} baris mutasi diunduh`);
    } catch (error) {
      console.error('Export admin mutasi error:', error);
      message.error('Gagal mengunduh mutasi saldo');
    }
  };

  const handleWithdrawal = (member) => {
    setSelectedMember(member);
    setWithdrawalModalVisible(true);
    withdrawalForm.resetFields();
    withdrawalForm.setFieldsValue({
      member_id: member.member_id,
      member_name: member.full_name,
      current_balance: member.deposit_balance
    });
  };

  const handleDeposit = (member) => {
    setSelectedMember(member);
    setDepositModalVisible(true);
    depositForm.resetFields();
    depositForm.setFieldsValue({
      member_id: member.member_id,
      member_name: member.full_name,
      current_balance: member.deposit_balance
    });
  };

  const handleWithdrawalSubmit = async (values) => {
    try {
      const response = await adminApi.post(`/api/merchant/finance/withdraw`, {
        user_id: selectedMember.id,
        amount: values.amount,
        reference_number: values.reference_number,
        notes: values.notes,
        beneficiary_name: values.beneficiary_name,
        beneficiary_account: values.beneficiary_account,
        beneficiary_bank: values.beneficiary_bank
      });

      message.success('Penarikan berhasil diproses');
      setWithdrawalModalVisible(false);
      fetchFinanceData();
    } catch (error) {
      message.error(error.response?.data?.error || 'Gagal memproses penarikan');
      console.error('Withdrawal error:', error);
    }
  };

  const handleDepositSubmit = async (values) => {
    try {
      const response = await adminApi.post(`/api/merchant/finance/deposit`, {
        user_id: selectedMember.id,
        amount: values.amount,
        payment_method: values.payment_method,
        reference_number: values.reference_number,
        notes: values.notes
      });

      message.success('Deposit berhasil diproses');
      setDepositModalVisible(false);
      fetchFinanceData();
    } catch (error) {
      message.error(error.response?.data?.error || 'Gagal memproses deposit');
      console.error('Deposit error:', error);
    }
  };

  const mutationColumns = [
    {
      title: 'Tanggal',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 160,
      render: (d) => moment(d).format('DD/MM/YYYY HH:mm:ss'),
    },
    {
      title: 'Jenis',
      dataIndex: 'transaction_type',
      key: 'transaction_type',
      width: 90,
      render: (t) => <Tag color={t === 'credit' ? 'green' : 'red'}>{(t || '').toUpperCase()}</Tag>,
    },
    {
      title: 'Jumlah',
      dataIndex: 'amount_change',
      key: 'amount_change',
      width: 140,
      align: 'right',
      render: (amt, r) => (
        <Text strong style={{ color: r.transaction_type === 'credit' ? '#52c41a' : '#cf1322' }}>
          {r.transaction_type === 'credit' ? '+' : '-'}{formatCurrency(Math.abs(amt))}
        </Text>
      ),
    },
    {
      title: 'Saldo Sebelum',
      dataIndex: 'previous_balance',
      key: 'previous_balance',
      width: 140,
      align: 'right',
      render: (a) => formatCurrency(a),
    },
    {
      title: 'Saldo Sesudah',
      dataIndex: 'new_balance',
      key: 'new_balance',
      width: 140,
      align: 'right',
      render: (a) => formatCurrency(a),
    },
    {
      title: 'Keterangan',
      dataIndex: 'description',
      key: 'description',
      ellipsis: true,
    },
    {
      title: 'Referensi',
      dataIndex: 'reference_id',
      key: 'reference_id',
      width: 200,
      ellipsis: true,
    },
  ];

  const memberColumns = [
    {
      title: 'ID Member',
      dataIndex: 'member_id',
      key: 'member_id',
      width: 120,
    },
    {
      title: 'Nama',
      dataIndex: 'full_name',
      key: 'full_name',
      width: 200,
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
      width: 200,
    },
    {
      title: 'Deposit Balance',
      dataIndex: 'deposit_balance',
      key: 'deposit_balance',
      width: 150,
      render: (amount) => (
        <Text strong style={{ color: '#52c41a' }}>
          {formatCurrency(amount)}
        </Text>
      ),
    },
    {
      title: 'Available Balance',
      dataIndex: 'available_balance',
      key: 'available_balance',
      width: 150,
      render: (amount) => formatCurrency(amount),
    },
    {
      title: 'Pending Balance',
      dataIndex: 'pending_balance',
      key: 'pending_balance',
      width: 150,
      render: (amount) => (
        <Text style={{ color: '#faad14' }}>
          {formatCurrency(amount)}
        </Text>
      ),
    },
    {
      title: 'KYC Status',
      dataIndex: 'kyc_status',
      key: 'kyc_status',
      width: 100,
      render: (status) => {
        const colorMap = {
          verified: 'green',
          unverified: 'orange',
          rejected: 'red'
        };
        return (
          <Tag color={colorMap[status] || 'default'}>
            {status?.toUpperCase()}
          </Tag>
        );
      },
    },
    {
      title: 'Aksi',
      key: 'action',
      fixed: 'right',
      width: 100,
      render: (_, record) => (
        <Space>
          <Tooltip title="Riwayat Mutasi Saldo">
            <Button
              icon={<HistoryOutlined />}
              size="small"
              onClick={() => handleViewMutations(record)}
            >
              Mutasi
            </Button>
          </Tooltip>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div style={{ marginBottom: '24px' }}>
        <Title level={2}>Merchant Finance Management</Title>
        <p style={{ color: '#666' }}>Kelola deposit balance dan transaksi keuangan merchant</p>
      </div>

      {/* Summary Cards */}
      {balanceData && (
        <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
          <Col xs={24} sm={12} md={6}>
            <Card>
              <Statistic
                title="Total Deposit Balance"
                value={balanceData.total_deposit_balance}
                prefix={<WalletOutlined style={{ color: '#52c41a' }} />}
                formatter={(value) => formatCurrency(value)}
                valueStyle={{ color: '#52c41a' }}
              />
              <Divider style={{ margin: '12px 0' }} />
              <Text type="secondary" style={{ fontSize: '12px' }}>
                <InfoCircleOutlined /> Saldo untuk cash out langsung
              </Text>
            </Card>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Card>
              <Statistic
                title="Total Available Balance"
                value={balanceData.total_available_balance}
                prefix={<BankOutlined style={{ color: '#1890ff' }} />}
                formatter={(value) => formatCurrency(value)}
                valueStyle={{ color: '#1890ff' }}
              />
              <Divider style={{ margin: '12px 0' }} />
              <Text type="secondary" style={{ fontSize: '12px' }}>
                <InfoCircleOutlined /> Saldo dari transaksi yang telah settle
              </Text>
            </Card>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Card>
              <Statistic
                title="Total Pending Balance"
                value={balanceData.total_pending_balance}
                prefix={<TransactionOutlined style={{ color: '#faad14' }} />}
                formatter={(value) => formatCurrency(value)}
                valueStyle={{ color: '#faad14' }}
              />
              <Divider style={{ margin: '12px 0' }} />
              <Text type="secondary" style={{ fontSize: '12px' }}>
                <InfoCircleOutlined /> Saldo menunggu settlement (H+0, H+1, H+2)
              </Text>
            </Card>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Card>
              <Statistic
                title="Total Gross Balance"
                value={balanceData.total_deposit_balance + balanceData.total_available_balance}
                prefix={<DollarOutlined style={{ color: '#722ed1' }} />}
                formatter={(value) => formatCurrency(value)}
                valueStyle={{ color: '#722ed1' }}
              />
              <Divider style={{ margin: '12px 0' }} />
              <Text type="secondary" style={{ fontSize: '12px' }}>
                <InfoCircleOutlined /> Total semua saldo merchant
              </Text>
            </Card>
          </Col>
        </Row>
      )}

      {/* Members Table */}
      <Card>
        <div style={{ marginBottom: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Title level={4}>Daftar Saldo Merchant</Title>
          <Button
            icon={<ReloadOutlined />}
            onClick={fetchFinanceData}
            loading={loading}
          >
            Refresh Data
          </Button>
        </div>

        <Table
          columns={memberColumns}
          dataSource={members}
          rowKey="id"
          loading={loading}
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} dari ${total} merchant`,
          }}
          scroll={{ x: 1200 }}
        />
      </Card>

      {/* Withdrawal Modal */}
      <Modal
        title="Penarikan Dana (Withdrawal)"
        open={withdrawalModalVisible}
        onCancel={() => setWithdrawalModalVisible(false)}
        footer={null}
        width={600}
      >
        <Form
          form={withdrawalForm}
          layout="vertical"
          onFinish={handleWithdrawalSubmit}
        >
          <Row gutter={[16, 0]}>
            <Col xs={24} sm={12}>
              <Form.Item label="ID Member" name="member_id">
                <Input disabled />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item label="Nama" name="member_name">
                <Input disabled />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item label="Saldo Deposit Saat Ini" name="current_balance">
            <Input
              disabled
              formatter={(value) => formatCurrency(value)}
            />
          </Form.Item>

          <Row gutter={[16, 0]}>
            <Col xs={24} sm={12}>
              <Form.Item
                label="Jumlah Penarikan"
                name="amount"
                rules={[
                  { required: true, message: 'Jumlah penarikan wajib diisi' },
                  { type: 'number', min: 10000, message: 'Minimal penarikan Rp 10.000' }
                ]}
              >
                <InputNumber
                  style={{ width: '100%' }}
                  formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                  parser={(value) => value.replace(/\$\s?|(,*)/g, '')}
                  placeholder="Masukkan jumlah penarikan"
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item
                label="No. Referensi"
                name="reference_number"
                rules={[{ required: true, message: 'No. referensi wajib diisi' }]}
              >
                <Input placeholder="WD-001" />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            label="Nama Penerima"
            name="beneficiary_name"
            rules={[{ required: true, message: 'Nama penerima wajib diisi' }]}
          >
            <Input placeholder="Nama lengkap penerima" />
          </Form.Item>

          <Row gutter={[16, 0]}>
            <Col xs={24} sm={12}>
              <Form.Item
                label="No. Rekening"
                name="beneficiary_account"
                rules={[{ required: true, message: 'No. rekening wajib diisi' }]}
              >
                <Input placeholder="1234567890" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item
                label="Bank"
                name="beneficiary_bank"
                rules={[{ required: true, message: 'Bank wajib diisi' }]}
              >
                <Select placeholder="Pilih bank">
                  <Option value="BCA">BCA</Option>
                  <Option value="BNI">BNI</Option>
                  <Option value="BRI">BRI</Option>
                  <Option value="MANDIRI">Mandiri</Option>
                  <Option value="BSI">BSI</Option>
                  <Option value="BANK_DK">Bank DKI</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Form.Item label="Catatan" name="notes">
            <Input.TextArea rows={3} placeholder="Catatan penarikan (opsional)" />
          </Form.Item>

          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <Space>
              <Button onClick={() => setWithdrawalModalVisible(false)}>
                Batal
              </Button>
              <Button type="primary" htmlType="submit" danger>
                Proses Penarikan
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Deposit Modal */}
      <Modal
        title="Tambah Deposit"
        open={depositModalVisible}
        onCancel={() => setDepositModalVisible(false)}
        footer={null}
        width={600}
      >
        <Form
          form={depositForm}
          layout="vertical"
          onFinish={handleDepositSubmit}
        >
          <Row gutter={[16, 0]}>
            <Col xs={24} sm={12}>
              <Form.Item label="ID Member" name="member_id">
                <Input disabled />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item label="Nama" name="member_name">
                <Input disabled />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item label="Saldo Deposit Saat Ini" name="current_balance">
            <Input
              disabled
              formatter={(value) => formatCurrency(value)}
            />
          </Form.Item>

          <Row gutter={[16, 0]}>
            <Col xs={24} sm={12}>
              <Form.Item
                label="Jumlah Deposit"
                name="amount"
                rules={[
                  { required: true, message: 'Jumlah deposit wajib diisi' },
                  { type: 'number', min: 10000, message: 'Minimal deposit Rp 10.000' }
                ]}
              >
                <InputNumber
                  style={{ width: '100%' }}
                  formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                  parser={(value) => value.replace(/\$\s?|(,*)/g, '')}
                  placeholder="Masukkan jumlah deposit"
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item
                label="Metode Pembayaran"
                name="payment_method"
                rules={[{ required: true, message: 'Metode pembayaran wajib dipilih' }]}
              >
                <Select placeholder="Pilih metode pembayaran">
                  <Option value="bank_transfer">Transfer Bank</Option>
                  <Option value="va">Virtual Account</Option>
                  <Option value="qris">QRIS</Option>
                  <Option value="ewallet">E-Wallet</Option>
                  <Option value="admin_adjustment">Adjustment Admin</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={[16, 0]}>
            <Col xs={24} sm={12}>
              <Form.Item
                label="No. Referensi"
                name="reference_number"
                rules={[{ required: true, message: 'No. referensi wajib diisi' }]}
              >
                <Input placeholder="DEP-001" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item label="Channel Pembayaran" name="payment_channel">
                <Input placeholder="BCA, BNI, OVO, Gopay, dll" />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item label="Catatan" name="notes">
            <Input.TextArea rows={3} placeholder="Catatan deposit (opsional)" />
          </Form.Item>

          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <Space>
              <Button onClick={() => setDepositModalVisible(false)}>
                Batal
              </Button>
              <Button type="primary" htmlType="submit">
                Tambah Deposit
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Modal Mutasi Saldo */}
      <Modal
        title={`Riwayat Mutasi Saldo${mutationMember ? ' — ' + (mutationMember.full_name || '') : ''}`}
        open={mutationModalVisible}
        onCancel={() => setMutationModalVisible(false)}
        footer={[
          <Button key="close" type="primary" onClick={() => setMutationModalVisible(false)}>
            Tutup
          </Button>
        ]}
        width={1100}
        destroyOnClose
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, gap: 8, flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <DatePicker.RangePicker
              value={mutationDateRange}
              onChange={(range) => { setMutationDateRange(range); if (mutationMember) loadMutations(mutationMember, range); }}
              format="YYYY-MM-DD"
              allowClear
            />
            {mutationTotal > 0 && <span style={{ color: '#888' }}>Total: {mutationTotal.toLocaleString('id-ID')} mutasi</span>}
          </div>
          <Button type="primary" ghost icon={<DownloadOutlined />} onClick={handleExportAdminMutations} disabled={!mutations.length}>
            Export CSV
          </Button>
        </div>
        <Table
          columns={mutationColumns}
          dataSource={mutations}
          rowKey="id"
          loading={mutationLoading}
          size="small"
          pagination={{ pageSize: 20, showTotal: (t) => `${t} mutasi` }}
          scroll={{ x: 1000 }}
          locale={{ emptyText: 'Belum ada riwayat mutasi saldo' }}
        />
      </Modal>
    </div>
  );
};

export default MerchantFinance;